import React, { useState, useEffect } from 'react';
import { X, Wallet, Calendar, Tag, FileText } from 'lucide-react';

const BudgetForm = ({ budget, apiKey, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    category: '',
    planned_amount: '',
    spent_amount: 0,
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const budgetCategories = [
    'Équipement',
    'Transport',
    'Salaires',
    'Marketing',
    'Formation',
    'Location Bureau',
    'Maintenance',
    'Fournitures',
    'Électricité',
    'Internet',
    'Autre'
  ];

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  useEffect(() => {
    if (budget) {
      setFormData(budget);
    }
  }, [budget]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const url = budget
        ? `${apiUrl}/api/finance/budgets/${budget.id}`
        : `${apiUrl}/api/finance/budgets/`;
      
      const method = budget ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          ...formData,
          month: parseInt(formData.month),
          year: parseInt(formData.year),
          planned_amount: parseFloat(formData.planned_amount),
          spent_amount: parseFloat(formData.spent_amount)
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || 'Erreur lors de l\'enregistrement');
      }

      onSave();
    } catch (err) {
      console.error('Error saving budget:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-6 flex justify-between items-center">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6" />
            {budget ? 'Modifier le Budget' : 'Nouveau Budget'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Period */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-amber-900 mb-3">Période du Budget</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Mois *
                </label>
                <select
                  required
                  value={formData.month}
                  onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  disabled={budget} // Can't change period for existing budget
                >
                  {months.map((month, index) => (
                    <option key={index} value={index + 1}>{month}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Année *
                </label>
                <input
                  type="number"
                  required
                  min="2020"
                  max="2100"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  disabled={budget} // Can't change period for existing budget
                />
              </div>
            </div>
            {budget && (
              <p className="text-xs text-amber-700 mt-2">
                Note: La période ne peut pas être modifiée
              </p>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Tag className="inline h-4 w-4 mr-1" />
              Catégorie *
            </label>
            <select
              required
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              disabled={budget} // Can't change category for existing budget
            >
              <option value="">Sélectionner une catégorie...</option>
              {budgetCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            {budget && (
              <p className="text-xs text-gray-500 mt-1">
                Note: La catégorie ne peut pas être modifiée
              </p>
            )}
          </div>

          {/* Amounts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Montant Planifié (USD) *
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.planned_amount}
                onChange={(e) => setFormData({ ...formData, planned_amount: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="1000"
              />
              <p className="text-xs text-gray-500 mt-1">Budget prévu pour cette catégorie</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Montant Dépensé (USD)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.spent_amount}
                onChange={(e) => setFormData({ ...formData, spent_amount: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-gray-50"
                placeholder="0"
                readOnly={!budget} // Only editable for existing budgets
              />
              <p className="text-xs text-gray-500 mt-1">
                {budget ? 'Ajustement manuel si nécessaire' : 'Calculé automatiquement'}
              </p>
            </div>
          </div>

          {/* Preview */}
          {formData.planned_amount > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Aperçu du Budget</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Montant planifié</span>
                  <span className="font-semibold text-gray-900">
                    ${parseFloat(formData.planned_amount || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Montant dépensé</span>
                  <span className="font-semibold text-red-600">
                    ${parseFloat(formData.spent_amount || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-blue-300">
                  <span className="text-gray-900 font-semibold">Restant</span>
                  <span className="font-bold text-green-600">
                    ${(parseFloat(formData.planned_amount || 0) - parseFloat(formData.spent_amount || 0)).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Notes (optionnel)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              rows="3"
              placeholder="Ex: Budget pour équipement Q4 2026"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              {isSubmitting ? 'Enregistrement...' : budget ? 'Modifier' : 'Créer le Budget'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BudgetForm;
